import { AxiosError } from "axios";
import {
  InfiniteQueryConfig,
  InfiniteQueryResult,
  MutationFunction,
  queryCache,
  QueryConfig,
  QueryFunction,
  QueryKey,
  QueryResult,
  ReactQueryMutationsConfig,
  useInfiniteQuery,
  useMutation,
  useQuery
} from "react-query";
import { QUERY_USERS_SESSION } from "./session";
import { useEffect } from "react";

const getShouldInvalidateSession = (error: any) =>
  !error?.response?.config.url.includes("session") &&
  error?.response?.status === 403;

export function useWSQuery<TResult = unknown, TError = unknown>(
  queryKey: QueryKey,
  queryFn: QueryFunction<TResult>,
  queryConfig?: QueryConfig<TResult, TError>
): QueryResult<TResult, TError> {
  return useQuery(queryKey, queryFn, {
    ...standardQueryOptions,
    ...queryConfig,
    retry: (_: number, error: any) => {
      if (getShouldInvalidateSession(error)) {
        return false;
      } else {
        // TODO(artemk): Remove as boolean
        return queryConfig?.retry as boolean;
      }
    },
    onError: (error: any) => {
      if (getShouldInvalidateSession(error)) {
        queryCache.invalidateQueries(QUERY_USERS_SESSION);
        queryCache.refetchQueries(QUERY_USERS_SESSION);
      } else {
        queryConfig?.onError?.(error);
      }
    }
  });
}

export function useWSInfiniteQuery<TResult = unknown, TError = unknown>(
  queryKey: QueryKey,
  queryFn: QueryFunction<TResult>,
  queryConfig?: InfiniteQueryConfig<TResult, TError>
): InfiniteQueryResult<TResult, TError> {
  return useInfiniteQuery(queryKey, queryFn, {
    ...standardQueryOptions,
    ...queryConfig,
    retry: (_: number, error: any) => {
      if (getShouldInvalidateSession(error)) {
        return false;
      } else {
        // TODO(artemk): Remove as boolean
        return queryConfig?.retry as boolean;
      }
    },
    onError: (error: any) => {
      if (getShouldInvalidateSession(error)) {
        queryCache.invalidateQueries(QUERY_USERS_SESSION);
        queryCache.refetchQueries(QUERY_USERS_SESSION);
      } else {
        queryConfig?.onError?.(error);
      }
    }
  });
}

export function useWSMutation<
  TResult,
  TError = AxiosError,
  TVariables = any,
  TSnapshot = any
>(
  mutationFunc: MutationFunction<TResult, TVariables>,
  config?: ReactQueryMutationsConfig<TResult, TError, TVariables, TSnapshot> & {
    dependencies?: QueryKey[];
  }
) {
  return useMutation(mutationFunc, {
    ...config,
    onSuccess: async (...args) => {
      const dependencies = config?.dependencies;
      if (dependencies) {
        for (const dependency of dependencies) {
          queryCache.invalidateQueries(dependency);
        }
      }
      await config?.onSuccess?.(...args);
    },
    onError: (error: any, ...args) => {
      if (getShouldInvalidateSession(error)) {
        queryCache.invalidateQueries(QUERY_USERS_SESSION);
        queryCache.refetchQueries(QUERY_USERS_SESSION);
      } else {
        config?.onError?.(error, ...args);
      }
    }
  });
}

export const standardQueryOptions: QueryConfig<any, any> = {
  retry: true,
  refetchOnWindowFocus: false,
  refetchOnMount: false,
  retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 5000)
};
